/**
 * The crafting dialog: DOM binding over {@link SkillCraftingModel}.
 *
 * Thin on purpose. Every rule about what may be shown or submitted lives in
 * the model, which is testable without a browser; this file only reads the
 * model and writes elements.
 *
 * The two behaviours here that are not decoration:
 *
 *   - **Opening the dialog takes the keyboard away from the game.** The
 *     canvas listens for `1`–`4` and WASD on `window`, so a dialog that only
 *     rendered on top would let a player cast a skill while typing a name.
 *     `onOpenChange` tells the host to disable input and drop any keys and
 *     charges that were being held.
 *   - **A late response never lands on the wrong account.** Every request
 *     captures the session version it started under, matching `BrowserUi`'s
 *     existing pattern, so a craft that resolves after a logout or an account
 *     switch is discarded instead of being shown to whoever is there now.
 */

import type { CraftOptionView, CraftAxis } from '../game/skill-crafting.js';
import { ApiError } from './stale-retry.js';
import type { CharacterView, MeView, SkillCraftView, SkillOptionsView, SkillPreviewView, SkillStatusMinimumsView } from '../game/http-types.js';
import type { SkillSummaryView } from '../game/skill-summary.js';
import { CRAFT_PREVIEW_DEBOUNCE_MS, CRAFT_STEPS, SkillCraftingModel, compactSummaryText, powerMultipleLabel, type CraftSelectionBody } from './skill-crafting-model.js';
import { CRAFT_POWER_PRESETS, type CraftPowerPreset } from '../game/craft-power.js';

export interface SkillCraftingApi {
  options(): Promise<SkillOptionsView>;
  craft(input: { revision: number; selection: CraftSelectionBody; powerMultiple: number; name: string; requestId: string }): Promise<SkillCraftView>;
  preview(input: { revision: number; selection: CraftSelectionBody; powerMultiple: number }): Promise<SkillPreviewView>;
  statusMinimums(input: { revision: number; selection: CraftSelectionBody; powerMultiple: number }): Promise<SkillStatusMinimumsView>;
  /** The character with its skill summaries; read for the owned list. */
  me(): Promise<MeView>;
  equip(input: { revision: number; slot: number; skillId: string | null }): Promise<CharacterView>;
}

export interface SkillCraftingHost {
  readonly api: SkillCraftingApi;
  /** Latest known character, or undefined when logged out. */
  character(): CharacterView | undefined;
  /** Bumped by the host whenever the account or session changes. */
  sessionVersion(): number;
  /** A newer character arrived; the HUD should take it. */
  onCharacter(character: CharacterView): void;
  /** The dialog opened or closed; the game loop should stop or resume input. */
  onOpenChange(open: boolean): void;
}

const AXES: readonly CraftAxis[] = ['source', 'shape', 'effect'];

const PRESET_LABELS: Readonly<Record<CraftPowerPreset, string>> = { HIGH_REPEAT: '고빈도', MID: '중간', HEAVY: '고위력' };

function byId<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error('missing UI element: ' + id);
  return found as T;
}

export class SkillCraftingUi {
  private readonly model = new SkillCraftingModel();
  private readonly panel = byId<HTMLElement>('craft');
  private readonly openButton = byId<HTMLButtonElement>('craft-open');
  private readonly hint = byId<HTMLElement>('craft-hint');
  private readonly nameInput = byId<HTMLInputElement>('craft-name');
  private readonly error = byId<HTMLElement>('craft-error');
  private readonly note = byId<HTMLElement>('craft-note');
  private readonly weaponNotice = byId<HTMLElement>('craft-weapon-notice');
  private readonly powerSlider = byId<HTMLInputElement>('craft-power');
  private readonly powerValue = byId<HTMLElement>('craft-power-value');
  private readonly powerPresets = byId<HTMLElement>('craft-power-presets');
  private readonly formOptions = byId<HTMLElement>('craft-form-options');
  private readonly shapeParams = byId<HTMLElement>('craft-shape-params');
  private shapeParamsKey = '';
  private readonly statuses = byId<HTMLElement>('craft-statuses');
  private statusesKey = '';
  private readonly previewName = byId<HTMLElement>('craft-preview-name');
  private readonly summary = byId<HTMLElement>('craft-summary');
  private readonly backButton = byId<HTMLButtonElement>('craft-back');
  private readonly nextButton = byId<HTMLButtonElement>('craft-next');
  private readonly submitButton = byId<HTMLButtonElement>('craft-submit');
  private readonly ownedList = byId<HTMLElement>('craft-owned-list');
  private readonly ownedCount = byId<HTMLElement>('craft-owned-count');
  private readonly previewLines = byId<HTMLElement>('craft-power-preview-lines');
  private readonly previewStatus = byId<HTMLElement>('craft-power-preview-status');
  private restAllowed = true;
  private catalogRequest = 0;
  private previewTimer: ReturnType<typeof setTimeout> | undefined;
  private minimumsTimer: ReturnType<typeof setTimeout> | undefined;
  /** The selection key whose status minimums are being asked for, so a re-render does not ask again. */
  private minimumsAsked: string | undefined;
  /** Owned-skill figures and the character revision they were computed for. */
  private summaries = new Map<string, SkillSummaryView>();
  private summariesRevision: number | undefined;
  private summariesRequestedRevision: number | undefined;

  constructor(private readonly host: SkillCraftingHost) {
    this.openButton.addEventListener('click', () => { void this.open(); });
    byId<HTMLButtonElement>('craft-close').addEventListener('click', () => this.close());
    this.backButton.addEventListener('click', () => { this.model.back(); this.render(); });
    this.nextButton.addEventListener('click', () => { this.model.advance(); this.render(); this.schedulePreview(0); });
    this.submitButton.addEventListener('click', () => { void this.submit(); });
    this.powerSlider.addEventListener('input', () => { this.model.setPower(Number(this.powerSlider.value)); this.render(); this.schedulePreview(CRAFT_PREVIEW_DEBOUNCE_MS); });
    for (const preset of Object.keys(CRAFT_POWER_PRESETS) as CraftPowerPreset[]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.preset = preset;
      button.addEventListener('click', () => { if (this.model.selectPreset(preset)) { this.render(); this.schedulePreview(CRAFT_PREVIEW_DEBOUNCE_MS); } });
      this.powerPresets.append(button);
    }
    this.nameInput.addEventListener('input', () => { this.model.setName(this.nameInput.value); this.render(); });
    // Escape closes the dialog rather than reaching the game, where it would
    // do nothing useful and the keypress would be ambiguous.
    this.panel.addEventListener('keydown', (event) => {
      if ((event as KeyboardEvent).key === 'Escape') { event.stopPropagation(); this.close(); }
    });
  }

  isOpen(): boolean { return !this.panel.hidden; }

  /** Build changes are only legal at the rest area; mirror that in the button. */
  setRestAllowed(allowed: boolean): void {
    // The host calls this every tick and on every focus change. Rendering on
    // an unchanged value would replace the option cards between a press and
    // its release, so the click would never reach a card.
    if (allowed === this.restAllowed) return;
    this.restAllowed = allowed;
    this.openButton.disabled = !allowed;
    this.hint.hidden = allowed;
    // The dialog is not force-closed on leaving the rest area: the player may
    // be mid-typing. The server re-checks, and submit is disabled below.
    if (this.isOpen()) this.render();
  }

  async open(): Promise<void> {
    if (!this.restAllowed) return;
    this.panel.hidden = false;
    this.host.onOpenChange(true);
    this.model.reset();
    // The held weapon may have changed since the last open; never show its catalog.
    this.model.setCatalog([]);
    this.nameInput.value = '';
    this.render();
    this.nameInput.focus();
    await this.loadCatalog();
  }

  close(): void {
    if (!this.isOpen()) return;
    clearTimeout(this.previewTimer);
    clearTimeout(this.minimumsTimer);
    this.minimumsAsked = undefined;
    this.panel.hidden = true;
    this.host.onOpenChange(false);
    // Focus goes back where it came from, not to the document body, so the
    // keyboard is usable without a mouse click.
    this.openButton.focus();
  }

  /** Called when the account changed or the player logged out. */
  dismissForSessionChange(): void {
    if (this.isOpen()) this.close();
    this.model.reset();
    this.summaries = new Map();
    this.summariesRevision = undefined;
    this.summariesRequestedRevision = undefined;
  }

  refreshOwned(): void {
    if (this.isOpen()) this.renderOwned();
  }

  private async loadCatalog(): Promise<void> {
    const version = this.host.sessionVersion();
    // Each open asks again because the held weapon may have changed; only the
    // newest request may land, or an older weapon's catalog could win the race.
    const request = ++this.catalogRequest;
    try {
      const value = await this.host.api.options();
      if (version !== this.host.sessionVersion() || request !== this.catalogRequest) return;
      this.model.setCatalog(value.options);
      this.render();
    } catch (error) {
      if (version === this.host.sessionVersion() && request === this.catalogRequest) this.showError(error);
    }
  }

  /**
   * Asks the server what the current POWER choice would produce. Debounced so
   * a drag is one request, and tokened by the model so only the newest answer
   * lands — a slow reply for an earlier value never overwrites the card.
   */
  private schedulePreview(delayMs: number): void {
    clearTimeout(this.previewTimer);
    if (this.model.currentStep() !== 'POWER') return;
    this.previewTimer = setTimeout(() => { void this.runPreview(); }, delayMs);
  }

  private async runPreview(): Promise<void> {
    const character = this.host.character();
    const body = this.model.previewBody();
    if (!character || !body || !this.isOpen() || this.model.currentStep() !== 'POWER') return;
    const version = this.host.sessionVersion();
    const token = this.model.beginPreview();
    this.renderPreview();
    try {
      const result = await this.host.api.preview({ revision: character.revision, ...body });
      if (version !== this.host.sessionVersion() || !this.model.completePreview(token, result.summary)) return;
    } catch (error) {
      if (version !== this.host.sessionVersion() || !this.model.failPreview(token, error instanceof Error ? error.message : '미리보기에 실패했습니다')) return;
    }
    this.renderPreview();
  }

  /**
   * Asks the server for the smallest share each status crafts at for the current selection and power. Debounced like the
   * preview; the model drops an answer for a selection that is no longer current.
   */
  private scheduleStatusMinimums(): void {
    const body = this.model.statusMinimumsBody();
    if (body === undefined || body.key === this.minimumsAsked) return;
    this.minimumsAsked = body.key;
    clearTimeout(this.minimumsTimer);
    this.minimumsTimer = setTimeout(() => { void this.runStatusMinimums(); }, CRAFT_PREVIEW_DEBOUNCE_MS);
  }

  private async runStatusMinimums(): Promise<void> {
    const character = this.host.character();
    const body = this.model.statusMinimumsBody();
    if (!character || !body || !this.isOpen()) return;
    const version = this.host.sessionVersion();
    const { key, ...request } = body;
    try {
      const result = await this.host.api.statusMinimums({ revision: character.revision, ...request });
      if (version !== this.host.sessionVersion() || !this.model.setStatusMinimums(key, result.minimums)) return;
    } catch (error) {
      if (version !== this.host.sessionVersion()) return;
      if (this.minimumsAsked === key) this.minimumsAsked = undefined;
      if (this.model.statusMinimumsFailureIsReportable(key, error instanceof ApiError ? error.code : undefined)) this.showError(error);
      return;
    }
    this.render();
    this.schedulePreview(0);
  }

  private renderPreview(): void {
    const state = this.model.previewState();
    this.previewLines.replaceChildren(...(state.summary ? this.model.previewLines(state.summary) : []).flatMap((line) => {
      const term = document.createElement('dt'); term.textContent = line.label;
      const value = document.createElement('dd'); value.textContent = line.value;
      return [term, value];
    }));
    this.previewStatus.textContent = state.loading ? '계산 중...' : state.error ?? (state.summary ? '현재 무기·스탯 기준 예상치입니다.' : '');
  }

  /** Re-reads the owned skills' figures when the character they were computed for is no longer the current one. */
  private async refreshSummaries(): Promise<void> {
    const character = this.host.character();
    if (!character || this.summariesRevision === character.revision || this.summariesRequestedRevision === character.revision) return;
    const version = this.host.sessionVersion();
    this.summariesRequestedRevision = character.revision;
    try {
      const me = await this.host.api.me();
      if (version !== this.host.sessionVersion()) return;
      this.setSummaries(me.character.revision, me.summaries);
      this.renderOwned();
    } catch {
      // Figures are a convenience; the list still works without them. A later
      // render asks again, because the requested revision is only remembered
      // for the duration of this attempt.
      if (this.summariesRequestedRevision === character.revision) this.summariesRequestedRevision = undefined;
    }
  }

  private setSummaries(revision: number, summaries: readonly SkillSummaryView[]): void {
    this.summaries = new Map(summaries.map((summary) => [summary.skillId, summary]));
    this.summariesRevision = revision;
  }

  private async submit(): Promise<void> {
    const character = this.host.character();
    if (!character || !this.model.canCraft()) return;
    const version = this.host.sessionVersion();
    // One id per submission attempt, so a retry after a lost response is
    // recognised by the server as the same craft rather than a second one.
    const requestId = `craft-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    this.model.beginCraft();
    this.error.textContent = '';
    this.render();
    try {
      const result = await this.host.api.craft({
        revision: character.revision,
        selection: this.model.selection(),
        powerMultiple: this.model.powerMultiple(),
        name: this.model.skillName().trim(),
        requestId,
      });
      if (version !== this.host.sessionVersion()) return;
      this.model.completeCraft({ skillId: result.skillId, name: this.model.skillName().trim(), sacrificed: result.sacrificed, summary: result.summary });
      this.setSummaries(result.character.revision, result.summaries);
      this.host.onCharacter(result.character);
      this.render();
    } catch (error) {
      if (version !== this.host.sessionVersion()) return;
      this.model.failCraft(error instanceof Error ? error.message : '제작에 실패했습니다');
      this.render();
    }
  }

  private async equip(skillId: string, slot: number): Promise<void> {
    const character = this.host.character();
    if (!character) return;
    const version = this.host.sessionVersion();
    try {
      const updated = await this.host.api.equip({ revision: character.revision, slot, skillId });
      if (version !== this.host.sessionVersion()) return;
      // Only after the server agrees. A locally-updated slot that the server
      // rejected would show an equipped skill the player does not have.
      this.host.onCharacter(updated);
      this.renderOwned();
    } catch (error) {
      if (version === this.host.sessionVersion()) this.showError(error);
    }
  }

  private showError(error: unknown): void {
    this.error.textContent = error instanceof Error ? error.message : '요청에 실패했습니다';
  }

  private render(): void {
    const step = this.model.currentStep();
    const phase = this.model.currentPhase();
    for (const name of CRAFT_STEPS) {
      byId<HTMLElement>(`craft-step-${name}`).hidden = name !== step || phase.kind === 'CRAFTED';
      byId<HTMLElement>(`craft-tab-${name}`).classList.toggle('active', name === step);
    }
    for (const axis of AXES) this.renderOptions(axis);
    this.renderShapeParams();
    this.renderStatuses();
    this.scheduleStatusMinimums();
    this.renderPower();
    const unsupported = this.model.noAvailableSource();
    this.weaponNotice.hidden = !unsupported;
    this.formOptions.hidden = unsupported;

    this.backButton.hidden = phase.kind === 'CRAFTED' || step === 'SOURCE';
    this.nextButton.hidden = phase.kind === 'CRAFTED' || step === 'CONFIRM' || unsupported;
    this.nextButton.disabled = !this.model.canAdvance();
    this.submitButton.hidden = phase.kind === 'CRAFTED' || step !== 'CONFIRM';
    this.submitButton.disabled = !this.model.canCraft() || !this.restAllowed;
    this.submitButton.textContent = phase.kind === 'SUBMITTING' ? '제작 중...' : '제작하기';
    this.nameInput.disabled = phase.kind === 'SUBMITTING' || phase.kind === 'CRAFTED';

    this.previewName.textContent = this.model.skillName().trim() || '이름 없는 기술';
    this.summary.replaceChildren(...(phase.kind === 'CRAFTED' ? [...this.model.summary().slice(0, 4), ...this.model.previewLines(phase.summary)] : this.model.summary()).flatMap((line) => {
      const term = document.createElement('dt'); term.textContent = line.label;
      const value = document.createElement('dd'); value.textContent = line.value;
      return [term, value];
    }));

    this.panel.classList.toggle('crafted', phase.kind === 'CRAFTED');
    if (phase.kind === 'CRAFTED') {
      // Only what the server reported, including its summary of the new skill.
      this.note.textContent = phase.sacrificed
        ? '제작 완료 — 일부 선호 조건을 지키지 못했습니다. 보유 스킬에서 확인하세요.'
        : '제작 완료 — 아래에서 슬롯에 장착하세요.';
      this.error.textContent = '';
    } else if (phase.kind === 'FAILED') {
      this.note.textContent = '';
      this.error.textContent = phase.message;
    } else {
      this.note.textContent = '제작 전 예상 구성입니다. 최종 성능은 제작 후 확정됩니다.';
      if (phase.kind !== 'SUBMITTING') this.error.textContent = this.error.textContent;
    }
    this.renderOwned();
  }

  /**
   * One slider per parameter of the chosen shape. The inputs are rebuilt only
   * when the shape changes: replacing a range input while it is being dragged
   * would drop the drag.
   */
  private renderShapeParams(): void {
    const sliders = this.model.shapeSliders();
    const key = `${this.model.chosenShape()?.optionId ?? ''}|${sliders.map(({ parameter }) => `${parameter.id}:${parameter.min}:${parameter.max}`).join(',')}`;
    if (key !== this.shapeParamsKey) {
      this.shapeParamsKey = key;
      this.shapeParams.replaceChildren(...sliders.map(({ parameter }) => {
        const row = document.createElement('label');
        row.className = 'craft-shape-param';
        const input = document.createElement('input');
        input.type = 'range';
        input.dataset.parameterId = parameter.id;
        input.min = String(parameter.min);
        input.max = String(parameter.max);
        input.step = String(parameter.step);
        const text = document.createElement('span');
        text.className = 'craft-shape-param-value';
        row.append(`${parameter.label} `, input, text);
        input.addEventListener('input', () => {
          this.model.setShapeValue(parameter.id, Number(input.value));
          this.render();
        });
        return row;
      }));
    }
    for (const { parameter, value } of sliders) {
      const input = this.shapeParams.querySelector<HTMLInputElement>(`[data-parameter-id="${CSS.escape(parameter.id)}"]`);
      if (input === null) continue;
      input.value = String(value);
      input.nextElementSibling!.textContent = ` ${value}${parameter.unit}`;
    }
  }

  /**
   * One row per status: a checkbox adds it, a slider sets the share of the
   * power it takes. Built once per catalog so a slider being dragged is not replaced.
   */
  private renderStatuses(): void {
    const rows = this.model.statusRows();
    const key = rows.map(({ option }) => option.optionId).join(',');
    if (key !== this.statusesKey) {
      this.statusesKey = key;
      this.statuses.replaceChildren(...rows.map(({ option, parameter }) => {
        const row = document.createElement('div');
        row.className = 'craft-status';
        row.dataset.optionId = option.optionId;
        const label = document.createElement('label');
        const check = document.createElement('input');
        check.type = 'checkbox';
        check.title = option.description;
        label.append(check, ` ${option.displayMetadata.icon} ${option.label}`);
        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = String(parameter.min);
        slider.max = String(parameter.max);
        slider.step = String(parameter.step);
        const text = document.createElement('span');
        text.className = 'craft-shape-param-value';
        row.append(label, slider, text);
        check.addEventListener('change', () => { this.model.toggleStatus(option.optionId); this.render(); });
        slider.addEventListener('input', () => { this.model.setStatusShare(option.optionId, Number(slider.value)); this.render(); });
        return row;
      }));
    }
    const pending = this.model.statusMinimumsPending();
    for (const { option, share, parameter, offered } of rows) {
      const row = this.statuses.querySelector<HTMLElement>(`[data-option-id="${CSS.escape(option.optionId)}"]`);
      if (row === null) continue;
      const [check, slider] = row.querySelectorAll<HTMLInputElement>('input');
      check!.checked = share !== undefined;
      check!.disabled = share === undefined && (pending || !offered);
      slider!.min = String(parameter.min);
      slider!.disabled = share === undefined || pending;
      if (share !== undefined) slider!.value = String(share);
      row.lastElementChild!.textContent = share === undefined ? '' : ` 위력의 ${Math.round(share * 100)}%`;
    }
  }

  private renderPower(): void {
    const { min, max, step } = this.model.powerRange();
    this.powerSlider.min = String(min);
    this.powerSlider.max = String(max);
    this.powerSlider.step = String(step);
    this.powerSlider.value = String(this.model.powerMultiple());
    this.powerValue.textContent = powerMultipleLabel(this.model.powerMultiple());
    this.renderPreview();
    for (const button of this.powerPresets.querySelectorAll<HTMLButtonElement>('button')) {
      const preset = button.dataset.preset as CraftPowerPreset;
      button.textContent = `${PRESET_LABELS[preset]} ×${CRAFT_POWER_PRESETS[preset]}`;
      button.disabled = !this.model.presetAvailable(preset);
      button.setAttribute('aria-pressed', String(this.model.powerMultiple() === CRAFT_POWER_PRESETS[preset]));
    }
  }

  private renderOptions(axis: CraftAxis): void {
    const container = byId<HTMLElement>(`craft-options-${axis}`);
    const chosen = this.model.selectionFor(axis);
    const active = document.activeElement;
    const focusedId = active instanceof HTMLElement && container.contains(active) ? active.dataset.optionId : undefined;
    container.replaceChildren(...this.model.optionsFor(axis).map((option) => this.optionCard(axis, option, chosen === option.optionId)));
    // Selecting with the keyboard re-renders the cards; keep focus on the same option.
    if (focusedId !== undefined) container.querySelector<HTMLElement>(`[data-option-id="${CSS.escape(focusedId)}"]`)?.focus();
  }

  private optionCard(axis: CraftAxis, option: CraftOptionView, selected: boolean): HTMLElement {
    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'craft-card';
    card.dataset.optionId = option.optionId;
    if (option.displayMetadata.accent) card.dataset.accent = option.displayMetadata.accent;
    card.classList.toggle('selected', selected);
    card.setAttribute('aria-pressed', String(selected));
    card.disabled = option.availability !== 'AVAILABLE';

    const icon = document.createElement('span'); icon.className = 'craft-card-icon'; icon.textContent = option.displayMetadata.icon;
    const label = document.createElement('span'); label.className = 'craft-card-label'; label.textContent = option.label;
    const description = document.createElement('span'); description.className = 'craft-card-note'; description.textContent = option.description;
    card.append(icon, label, description);
    if (option.availability !== 'AVAILABLE') {
      // Colour alone must never carry this. The reason is text.
      const reason = document.createElement('span');
      reason.className = 'craft-card-disabled';
      reason.textContent = option.disabledReason ?? '아직 제작할 수 없습니다';
      card.append(reason);
    }
    card.addEventListener('click', () => { if (this.model.select(axis, option.optionId)) this.render(); });
    return card;
  }

  private renderOwned(): void {
    const character = this.host.character();
    const skills = character?.skills ?? [];
    const unavailable = character?.quarantinedSkills ?? [];
    this.ownedCount.textContent = `보유 스킬 ${skills.length + unavailable.length} / 32`;
    this.ownedList.replaceChildren(...skills.map((skill) => {
      const item = document.createElement('li');
      const title = document.createElement('p');
      title.className = 'craft-owned-name';
      const slot = character?.equippedSkillIds.findIndex((id) => id === skill.id) ?? -1;
      title.textContent = slot >= 0 ? `${skill.name} — ${slot + 1}번 슬롯` : `${skill.name} — 미장착`;
      const slots = document.createElement('div');
      slots.className = 'craft-slot-row';
      for (let index = 0; index < 4; index += 1) {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = String(index + 1);
        button.disabled = slot === index;
        button.setAttribute('aria-label', `${skill.name}을 ${index + 1}번 슬롯에 장착`);
        button.addEventListener('click', () => { void this.equip(skill.id, index); });
        slots.append(button);
      }
      const stats = document.createElement('p');
      stats.className = 'craft-owned-stats';
      const summary = this.summaries.get(skill.id);
      stats.textContent = summary ? compactSummaryText(summary) : '';
      const text = document.createElement('div');
      text.append(title, stats);
      item.append(text, slots);
      return item;
    }), ...unavailable.map((skill) => {
      const item = document.createElement('li');
      item.className = 'craft-owned-unavailable';
      const title = document.createElement('p');
      title.className = 'craft-owned-name';
      title.textContent = `${skill.name} — 사용 불가`;
      const reason = document.createElement('p');
      reason.className = 'craft-owned-stats';
      reason.textContent = skill.quarantine.reasonLabel;
      const text = document.createElement('div');
      text.append(title, reason);
      item.append(text);
      return item;
    }));
    void this.refreshSummaries();
  }
}
